import type { UserRole } from "@/dashboard/features/userManagement/lib/schema";
import type { User } from "@/dashboard/types/user";

/**
 * Get the user's role for a specific tenant.
 * Super admins are treated as admins for all tenants.
 */
export function getRoleForTenant(user: User, tenantId: string): UserRole | null {
  if (user.superAdmin) {
    return "admin";
  }

  // Call-taker users have member-level access to all tenants
  if (user.neticRole === "call_taker") {
    return "member";
  }

  return user.tenantPermissions[tenantId]?.role ?? null;
}

/**
 * Check if user has at least the required role for a specific tenant.
 * Uses role hierarchy: member < manager < admin
 */
export function hasRoleForTenant(user: User, tenantId: string, requiredRole: UserRole): boolean {
  const userRole = getRoleForTenant(user, tenantId);

  if (!userRole) {
    return false;
  }

  const roleHierarchy: Record<UserRole, number> = {
    non_dashboard_user: 0,
    analytics_viewer: 0,
    member: 1,
    manager: 2,
    admin: 3,
  };

  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
}

/**
 * Check if user has at least the required role for ANY tenant in their account.
 * Useful for account-level permissions.
 */
/**
 * Check if the user only has non_dashboard_user roles (e.g. Greenhouse-only).
 * Super admins are never considered non-dashboard users.
 */
export function isNonDashboardUser(user: Pick<User, "superAdmin" | "tenantPermissions">): boolean {
  if (user.superAdmin) return false;
  const permissions = Object.values(user.tenantPermissions);
  return permissions.length > 0 && permissions.every(p => p.role === "non_dashboard_user");
}

/**
 * Check if the user only has analytics_viewer roles.
 * Super admins are never considered analytics-only users.
 */
export function isAnalyticsViewer(user: Pick<User, "superAdmin" | "tenantPermissions">): boolean {
  if (user.superAdmin) {
    return false;
  }
  const permissions = Object.values(user.tenantPermissions);
  return permissions.length > 0 && permissions.every(p => p.role === "analytics_viewer");
}

export function hasRoleForAnyTenant(user: User, requiredRole: UserRole): boolean {
  if (user.superAdmin) {
    return true;
  }

  const roleHierarchy: Record<UserRole, number> = {
    non_dashboard_user: 0,
    analytics_viewer: 0,
    member: 1,
    manager: 2,
    admin: 3,
  };

  const requiredLevel = roleHierarchy[requiredRole];

  // Check if user has the required role in any tenant
  return Object.values(user.tenantPermissions).some(
    perm => roleHierarchy[perm.role] >= requiredLevel
  );
}
